import { createElement } from 'react';
import { toast } from 'sonner';

import { ErrorToast } from '@/shared/components/ui/sonner';
import { Sentry } from './sentry';
import { ApiError } from './api';
import { describeError } from './errors';
import { i18n } from './i18n';
import { reportNetworkState } from './network-status';

interface ErrorOptions {
  /** What failed, specific to the screen — replaces the title derived from the code. */
  title?: string;
  onRetry?: () => void;
}

/**
 * Toasts for the whole app (DESIGN.md §8). Errors go through describeError:
 * never `err.message` on screen. Network failures raise the offline banner
 * instead of a toast.
 */
export const notify = {
  error(err: unknown, options: ErrorOptions = {}): void {
    const described = describeError(err, i18n.t);
    if (described.category === 'network') {
      reportNetworkState(false);
      return;
    }
    // The caller's title is more specific than any generic translation
    // („Stoc insuficient pentru Aluminiu: 12 kg disponibile”).
    if (options.title) described.title = options.title;
    if (described.category === 'unexpected' && err instanceof Error) {
      // A frontend bug the user can't act on; the report is what gets it fixed.
      Sentry.captureException(err);
    }
    // Same failure from several requests at once → one toast, not ten.
    const id = `error:${err instanceof ApiError ? err.code : described.title}:${window.location.pathname}`;
    toast.custom((toastId) => createElement(ErrorToast, { id: toastId, error: described, onRetry: options.onRetry }), {
      id,
      duration: Infinity,
    });
  },
  success(message: string, options: { description?: string; action?: { label: string; onClick: () => void } } = {}): void {
    toast.success(message, { description: options.description, action: options.action });
  },
  info(message: string, options: { description?: string } = {}): void {
    toast.info(message, { description: options.description });
  },
  warning(message: string, options: { description?: string } = {}): void {
    toast.warning(message, { description: options.description });
  },
};
